/**
 * The 44px column at the end of a task row, and the same menu on a board card.
 *
 * The status used to be a `<Select>` sitting inside the card, which drew the
 * operating system's grey popup on top of the board. It is a menu now, for the
 * same reason `MenuField` exists, and it moved in here so a card carries one
 * control rather than two.
 *
 * No permission check: `/tasks` already answers to `manage:crm.projects` in
 * `crm/nav.ts`, so anybody who can reach this row can also write to it.
 */
import { Link } from "react-router-dom";
import { ArrowUpRight, Check, MoreHorizontal, Trash2 } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import type { Task, TaskStatus } from "../../../lib/projects";
import { Pop, useClosePops } from "../../shell/Pop";
import { TASK_STATUSES } from "../projects/status";
import { TimerMenuItem } from "../../timer";

export function TaskRowMenu({
  task,
  onMove,
  onDelete,
  className,
  small,
}: {
  task: Task;
  onMove: (s: TaskStatus) => void;
  onDelete: () => void;
  className?: string;
  small?: boolean;
}) {
  const t = useT();
  const close = useClosePops();

  return (
    // Above the row-covering link overlay, or the menu would never get the click.
    <span className={cn("relative z-[1] justify-self-end", className)}>
      <Pop
        trigger={(open) => (
          <button
            type="button"
            className={cn("crm-cb", small && "crm-cb-sm", open && "is-on")}
            title={t("common.more")}
            aria-label={t("common.more")}
            aria-haspopup="menu"
            aria-expanded={open}
          >
            <MoreHorizontal className={small ? "size-3.5" : "size-4"} />
          </button>
        )}
      >
        {/* A real Link rather than a button that navigates: the row's own
            stretched overlay is not reachable from inside the menu, and this
            keeps middle-click and "open in new tab" working. `Pop`'s roving
            focus counts `a[href]` as a row, so it stays keyboard-reachable. */}
        <Link className="crm-mi" to={`/tasks/${task.id}`} onClick={close}>
          <ArrowUpRight className="size-4" />
          {t("tasks.open")}
        </Link>

        {/* The clock, directly under "open" and above the status list: it is
            the action an operator takes on a task without leaving the register,
            and it draws nothing at all for anybody who cannot run one. */}
        <TimerMenuItem taskId={task.id} onDone={close} />

        <div className="crm-menu-sep" />
        <div className="crm-menu-head">
          <span className="crm-menu-t">{t("tasks.moveTo")}</span>
        </div>
        {TASK_STATUSES.map((s) => (
          <button
            key={s}
            type="button"
            className={cn("crm-mi", s === task.status && "is-on")}
            onClick={() => {
              onMove(s);
              close();
            }}
          >
            <span className="flex-1">{t(`tasks.status.${s}`)}</span>
            {s === task.status ? <Check className="size-4" /> : null}
          </button>
        ))}

        <div className="crm-menu-sep" />
        <button
          type="button"
          className="crm-mi crm-mi-danger"
          onClick={() => {
            close();
            onDelete();
          }}
        >
          <Trash2 className="size-4" />
          {t("common.delete")}
        </button>
      </Pop>
    </span>
  );
}
